/**
 * API istemcisi.
 *
 * ── ACCESS TOKEN BELLEKTE, DISKTE DEGIL ──────────────────────────────────────
 * Access token `localStorage`'a YAZILMAZ. localStorage'i her JavaScript okur;
 * tek bir XSS acigi tum oturumu calar. Bellekte tutulan token, sayfa
 * yenilendiginde kaybolur — ama refresh cerezi httpOnly oldugu icin oturum
 * sessizce geri kurulur. Kullanici farki gormez, saldirgan token'i goremez.
 *
 * ── 401 -> TEK SEFER YENILE -> TEKRAR DENE ───────────────────────────────────
 * Token suresi dolduysa istemci bunu bir hata gibi gostermez; sessizce yeniler
 * ve istegi tekrarlar. Es zamanli 5 istek 401 alirsa BIR yenileme yapilir
 * (`refreshInFlight`), bes degil — aksi halde token ailesi gereksiz yere
 * doner ve yeniden-kullanim tespiti yanlislikla tetiklenebilir.
 */

import type { ApiError } from '@shared/index';

const BASE_URL = '/api';

let accessToken: string | null = null;
let refreshInFlight: Promise<boolean> | null = null;
let requestLanguage: 'tr' | 'en' = (() => {
  try { return localStorage.getItem('wondeon.language') === 'en' ? 'en' : 'tr'; }
  catch { return 'tr'; }
})();

/** Oturum tamamen bittiginde tetiklenir — AuthProvider giris ekranina yollar. */
type SessionEndedHandler = () => void;
let onSessionEnded: SessionEndedHandler = () => {};

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Katalog/CMS metinlerinin sunucudan hangi dilde döneceğini belirler. */
export function setRequestLanguage(language: 'tr' | 'en'): void {
  requestLanguage = language;
}

export function setSessionEndedHandler(handler: SessionEndedHandler): void {
  onSessionEnded = handler;
}

/** Sunucunun dondurdugu, KULLANICIYA GOSTERILEBILIR hata. */
export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    override readonly message: string,
    readonly fields?: Record<string, string>,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }

  /** Form alanina bagli hata var mi? */
  fieldError(name: string): string | undefined {
    return this.fields?.[name];
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  /** Ic kullanim: yenileme sonrasi sonsuz donguyu engeller. */
  _retried?: boolean;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(BASE_URL + path, window.location.origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== '') url.searchParams.set(key, String(value));
    }
  }
  return url.pathname + url.search;
}

/**
 * Refresh akisi. Ayni anda yalnizca BIR tane calisir.
 * @returns yenileme basarili mi
 */
async function refresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const res = await fetch(BASE_URL + '/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Refresh cerezi httpOnly; `credentials: include` olmadan gonderilmez.
        credentials: 'include',
        body: JSON.stringify({ client: 'web' }),
      });

      if (!res.ok) {
        accessToken = null;
        return false;
      }

      const data = (await res.json()) as { accessToken?: string };
      if (!data.accessToken) {
        accessToken = null;
        return false;
      }

      accessToken = data.accessToken;
      return true;
    } catch {
      accessToken = null;
      return false;
    } finally {
      // Bir sonraki 401 yeni bir yenileme baslatabilsin.
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();

  return refreshInFlight;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal } = options;

  // Dosya yuklemede govde FormData olur. Content-Type'i ELLE KOYMAYIZ:
  // tarayici multipart sinir dizesini kendisi uretir, biz yazarsak sinir
  // eksik kalir ve sunucu govdeyi ayristiramaz.
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

  const headers: Record<string, string> = {};
  headers['Accept-Language'] = requestLanguage;
  if (body !== undefined && !isFormData) headers['Content-Type'] = 'application/json';
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  const res = await fetch(buildUrl(path, query), {
    method,
    headers,
    credentials: 'include',
    ...(body !== undefined
      ? { body: isFormData ? (body as FormData) : JSON.stringify(body) }
      : {}),
    ...(signal ? { signal } : {}),
  });

  // OTURUM KURMA ucları bu akışın DIŞINDA.
  //
  // Bu uçlarda 401 "token'ın süresi doldu" demek değil, "kimlik bilgisi yanlış"
  // demektir — henüz bir oturum yok ki bitsin. Ayrım yapılmadığında yanlış
  // parola giren kullanıcı sunucunun gerçek mesajı ("E-posta veya parola
  // hatalı.") yerine "Oturumun sona erdi, tekrar giriş yap." görüyordu; üstelik
  // arkada anlamsız bir /auth/refresh denemesi de tetikleniyordu. Kullanıcıyı
  // tamamen yanlış teşhise itiyordu: parola hatası, oturum hatası gibi görünüyor.
  const oturumKurmaUcu =
    path === '/auth/login' ||
    path === '/auth/register' ||
    path === '/auth/refresh' ||
    path === '/auth/google/consent';

  if (res.status === 401 && !options._retried && !oturumKurmaUcu) {
    const ok = await refresh();
    if (ok) return request<T>(path, { ...options, _retried: true });

    // Yenileme de basarisiz: oturum gercekten bitti.
    onSessionEnded();
    throw new ApiRequestError(401, 'TOKEN_EXPIRED', 'Oturumun sona erdi, tekrar giriş yap.');
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const payload: unknown = text ? safeJson(text) : null;

  if (!res.ok) {
    const err = (payload as ApiError | null)?.error;
    throw new ApiRequestError(
      res.status,
      err?.code ?? 'UNKNOWN',
      // Sunucu her zaman Turkce, gosterilebilir bir mesaj doner. Donmediyse
      // bu bir sunucu hatasidir; kullaniciya yine de anlamli bir sey soyluyoruz.
      err?.message ?? 'Beklenmeyen bir hata oluştu. Lütfen tekrar dene.',
      err?.fields,
    );
  }

  return payload as T;
}

export interface ApiStreamEvent<T = unknown> {
  id?: string;
  event: string;
  data: T;
}

/**
 * Kimlik doğrulamalı özel dosyayı indirir.
 *
 * Doğrudan `<a href>` kullanılamaz; access token diskte değil bellektedir ve
 * bağlantı Authorization başlığı göndermez. Bu yardımcı aynı 401→refresh
 * sözleşmesini koruyarak Blob üretir.
 */
async function download(path: string, retried = false): Promise<{ blob: Blob; filename: string }> {
  const headers: Record<string, string> = { 'Accept-Language': requestLanguage };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  const response = await fetch(buildUrl(path), { headers, credentials: 'include' });
  if (response.status === 401 && !retried) {
    if (await refresh()) return download(path, true);
    onSessionEnded();
    throw new ApiRequestError(401, 'TOKEN_EXPIRED', 'Oturumun sona erdi, tekrar giriş yap.');
  }
  if (!response.ok) {
    const text = await response.text();
    const payload = text ? safeJson(text) as ApiError | null : null;
    throw new ApiRequestError(response.status, payload?.error?.code ?? 'DOWNLOAD_FAILED', payload?.error?.message ?? 'Dosya indirilemedi.');
  }
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const utf8 = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const plain = disposition.match(/filename="([^"]+)"/i)?.[1];
  return { blob: await response.blob(), filename: utf8 ? decodeURIComponent(utf8) : plain ?? 'attachment' };
}

/**
 * Authorization başlığı gerektiği için native EventSource yerine fetch akışı
 * kullanılır. Access token yine yalnız bellekte kalır; 401 alınırsa normal API
 * istemcisiyle aynı tekil refresh kapısından geçilir.
 */
export async function streamEvents<T>(
  path: string,
  onEvent: (event: ApiStreamEvent<T>) => void,
  signal: AbortSignal,
  retried = false,
): Promise<void> {
  const headers: Record<string, string> = {
    Accept: 'text/event-stream',
    'Accept-Language': requestLanguage,
  };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  const response = await fetch(buildUrl(path), { headers, credentials: 'include', signal });
  if (response.status === 401) {
    if (!retried && await refresh()) {
      return streamEvents(path, onEvent, signal, true);
    }
    onSessionEnded();
    throw new ApiRequestError(401, 'TOKEN_EXPIRED', 'Oturumun sona erdi, tekrar giriş yap.');
  }
  if (!response.ok || !response.body) {
    throw new ApiRequestError(response.status, 'STREAM_CONNECTION_FAILED', 'Canlı güncelleme bağlantısı kurulamadı.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (!signal.aborted) {
    const { value, done } = await reader.read();
    buffer += decoder.decode(value, { stream: !done }).replace(/\r\n/g, '\n');
    let boundary = buffer.indexOf('\n\n');
    while (boundary >= 0) {
      const block = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      const parsed = parseStreamBlock<T>(block);
      if (parsed) onEvent(parsed);
      boundary = buffer.indexOf('\n\n');
    }
    if (done) break;
  }
}

function parseStreamBlock<T>(block: string): ApiStreamEvent<T> | null {
  let id: string | undefined;
  let event = 'message';
  const data: string[] = [];
  for (const line of block.split('\n')) {
    if (!line || line.startsWith(':') || line.startsWith('retry:')) continue;
    if (line.startsWith('id:')) id = line.slice(3).trimStart();
    else if (line.startsWith('event:')) event = line.slice(6).trimStart();
    else if (line.startsWith('data:')) data.push(line.slice(5).trimStart());
  }
  if (data.length === 0) return null;
  const raw = data.join('\n');
  try { return { ...(id ? { id } : {}), event, data: JSON.parse(raw) as T }; }
  catch { return { ...(id ? { id } : {}), event, data: raw as T }; }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query']) =>
    request<T>(path, query ? { query } : {}),
  post: <T>(path: string, body?: unknown, query?: RequestOptions['query']) =>
    request<T>(path, { method: 'POST', ...(body !== undefined ? { body } : {}), ...(query ? { query } : {}) }),
  patch: <T>(path: string, body?: unknown, query?: RequestOptions['query']) =>
    request<T>(path, { method: 'PATCH', ...(body !== undefined ? { body } : {}), ...(query ? { query } : {}) }),
  /** Dosya yukleme — govde FormData, Content-Type'i tarayici belirler. */
  upload: <T>(path: string, form: FormData) => request<T>(path, { method: 'POST', body: form }),
  put: <T>(path: string, body?: unknown, query?: RequestOptions['query']) =>
    request<T>(path, { method: 'PUT', ...(body !== undefined ? { body } : {}), ...(query ? { query } : {}) }),
  delete: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'DELETE', ...(body !== undefined ? { body } : {}) }),
  download,
  /** Uygulama acilisinda: cerez varsa oturumu geri kurar. */
  restoreSession: refresh,
  stream: streamEvents,
};
